import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { ROLE_PRESETS, PermissionKey } from '@/types/permission';
import type { TenantModules, TenantModuleKey } from '@/types/tenant';

// Plan tiers: trial=0, starter=1, professional=2, enterprise=3, unlimited(scale)=4
const PLAN_TIERS: Record<string, number> = { trial: 0, starter: 1, professional: 2, enterprise: 3, unlimited: 4 };

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tenantModules: TenantModules | null;
  tenantPlan: string;

  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setTenantModules: (modules: TenantModules | null) => void;
  setTenantPlan: (plan: string) => void;
  logout: () => void;

  // Permission helpers
  hasPermission: (permission: PermissionKey) => boolean;
  hasAnyPermission: (permissions: PermissionKey[]) => boolean;
  hasAllPermissions: (permissions: PermissionKey[]) => boolean;
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;

  // Module helpers
  hasModule: (module: TenantModuleKey) => boolean;

  // Plan helpers
  hasPlanTier: (minPlan: number) => boolean;
  getPlanTier: () => number;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      tenantModules: null,
      tenantPlan: 'trial',

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setToken: (token) => set({ token }),

      setLoading: (isLoading) => set({ isLoading }),

      setTenantModules: (tenantModules) => set({ tenantModules }),

      setTenantPlan: (tenantPlan) => set({ tenantPlan }),

      logout: () => {
        // Clear persisted state immediately to prevent stale data on next login
        localStorage.removeItem('roni-panel-auth');
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          tenantModules: null,
        });
      },

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;

        // Super admin has all permissions
        if (user.role === 'super_admin') return true;

        // Check role preset permissions
        const rolePermissions = ROLE_PRESETS[user.role as keyof typeof ROLE_PRESETS] || [];
        if ((rolePermissions as readonly string[]).includes(permission)) return true;

        // Check custom user permissions
        return user.permissions?.[permission] === true;
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get();
        return permissions.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissions) => {
        const { hasPermission } = get();
        return permissions.every((p) => hasPermission(p));
      },

      isSuperAdmin: () => {
        const { user } = get();
        return user?.role === 'super_admin';
      },

      isTenantAdmin: () => {
        const { user } = get();
        return user?.role === 'tenant_admin' || user?.role === 'super_admin';
      },

      hasModule: (module) => {
        const { user, tenantModules } = get();
        if (!user) return false;
        if (user.role === 'super_admin') return true;
        if (!tenantModules || Object.keys(tenantModules).length === 0) return true;
        return tenantModules[module] === true;
      },

      hasPlanTier: (minPlan: number) => {
        const { user, tenantPlan } = get();
        if (!user) return false;
        if (user.role === 'super_admin') return true;
        // If tenantPlan is not set (stale session), grant full access until next login refreshes it
        if (!tenantPlan) return true;
        return (PLAN_TIERS[tenantPlan] ?? 0) >= minPlan;
      },

      getPlanTier: () => {
        const { tenantPlan } = get();
        return PLAN_TIERS[tenantPlan] ?? 0;
      },
    }),
    {
      name: 'roni-panel-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        tenantModules: state.tenantModules,
        tenantPlan: state.tenantPlan,
      }),
    }
  )
);
